import Slider from "react-slick";
import { useState, useRef, useMemo, useEffect } from "react";
import Widget from "../../Widget/Widget";
import SettingsModal from "../../Modal/SettingsModal";
import { BenefitsChartModal } from "./BenefitsChartSettings/BenefitsChartModal";
import { Message, Empty } from "../../BP2Components";
import { getListV2 } from "../../../services/api/Api";
import { getListV3 } from "../../../services/restAPI/ListsV2";
import { useIntl } from "react-intl";
import clsx from "clsx";
import { DonutChart, DonutSkeleton } from "./BenefitsDonutCharts/BenefitsDonutCharts";
import "./BenefitsChartWidget.less";

type WidgetValue = {
   name: string;
   id: string;
   listId: string;
};

type Props = {
   value: WidgetValue;
   data: unknown;
   role: string;
};

type ApiChartItem = {
   id: string;
   order?: number;
   "group Name"?: string;
   dashboard?: string;
   "chart Type"?: string;
   "chart Title"?: string;
   "chart Text"?: number;
   "chart Json"?: string;
};

type DonutSlice = { label: string; value: number; color: string };

const parseChartJson = (raw?: string): DonutSlice[] => {
   if (!raw) return [];
   try {
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];

      return arr.map((chart) => {
         const parsedValue = Number(chart?.value ?? 0);
         return {
            label: String(chart?.name ?? ""),
            value: Number.isFinite(parsedValue) ? parsedValue : 0,
            color: String(chart?.color ?? "#E6EEF6"),
         };
      });
   } catch {
      return [];
   }
};

const CustomPrevArrow = (props: any) => {
   const { onClick, className } = props;
   return (
      <button className={className} onClick={onClick} type="button">
         <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
               d="M15 18L9 12L15 6"
               stroke="currentColor"
               strokeWidth="1.2"
               strokeLinecap="round"
               strokeLinejoin="round"
            />
         </svg>
      </button>
   );
};

const CustomNextArrow = (props: any) => {
   const { onClick, className } = props;
   return (
      <button className={className} onClick={onClick} type="button">
         <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
               d="M9 6L15 12L9 18"
               stroke="currentColor"
               strokeWidth="1.2"
               strokeLinecap="round"
               strokeLinejoin="round"
            />
         </svg>
      </button>
   );
};

export const BenefitsChartWidget = ({ value, data, role }: Props) => {
   const [benefitIndex, setBenefitIndex] = useState<number>(0);
   const [rows, setRows] = useState<ApiChartItem[]>([]);
   const [isLoading, setIsLoading] = useState(false);
   const sliderRef = useRef<Slider | null>(null);
   const intl = useIntl();

   const { name, id, listId } = value;
   const isV3 = listId ? listId.toString().length > 10 : false;

   useEffect(() => {
      if (!listId) {
         setRows([]);
         setBenefitIndex(0);
         setIsLoading(false);
         return;
      }
      let cancelled = false;

      const fetchData = async () => {
         setBenefitIndex(0);
         setIsLoading(true);
         try {
            if (isV3) {
               const res = await getListV3(listId, 1, 10);
               if (res.status !== 200) {
                  Message.error(intl.formatMessage({ id: "error.loadingListData" }));
                  if (!cancelled) setRows([]);
               } else {
                  if (!cancelled) setRows(Array.isArray(res.data.data) ? res.data.data : []);
               }
            } else {
               const res = await getListV2(listId, 1, 10);
               if (res.status !== 200) {
                  Message.error(intl.formatMessage({ id: "error.loadingListData" }));
                  if (!cancelled) setRows([]);
               } else {
                  if (!cancelled)
                     setRows(Array.isArray(res.data.default.data) ? res.data.default.data : []);
               }
            }
         } catch {
            Message.error(intl.formatMessage({ id: "error.loadingListData" }));
            if (!cancelled) setRows([]);
         } finally {
            if (!cancelled) setIsLoading(false);
         }
      };

      void fetchData();
      return () => {
         cancelled = true;
      };
   }, [listId]);

   const sortedRows = useMemo(() => {
      return [...rows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
   }, [rows]);

   const slidesCount = sortedRows.length;

   const sliderSettings = useMemo(
      () => ({
         nextArrow: <CustomNextArrow />,
         prevArrow: <CustomPrevArrow />,
         infinite: slidesCount > 2,
         speed: 600,
         slidesToScroll: 1,
         swipe: true,
         dots: false,
         draggable: true,
         beforeChange: (_: number, newIndex: number) => {
            setBenefitIndex(newIndex);
         },
         slidesToShow: Math.min(2, slidesCount),
         responsive: [
            {
               breakpoint: 1024,
               settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                  infinite: slidesCount > 1,
               },
            },
         ],
      }),
      [slidesCount],
   );

   return (
      <Widget
         role={role}
         bgColor="transparent"
         overflow="visible"
         className="benefits-chart__widget"
         extra={
            (role === "OWNER" || role === "MODERATOR") && (
               //@ts-ignore
               <SettingsModal widgetName={name} widgetId={id} icon="setting" deleteIcon="true">
                  <BenefitsChartModal value={value} data={data} />
               </SettingsModal>
            )
         }
      >
         <div className="benefits-chart-redesign">
            {!isLoading && sortedRows.length === 0 && (
               <div className="benefits-chart__empty-card">
                  <Empty />
               </div>
            )}
            <div className="benefits-chart__slider-wrapper">
               {isLoading && (
                  <div className="benefits-chart__skeleton-row">
                     <DonutSkeleton />
                     <DonutSkeleton />
                  </div>
               )}
               {!isLoading && sortedRows.length > 0 && (
                  <Slider ref={sliderRef} {...sliderSettings}>
                     {sortedRows.map((row, index) => {
                        const slices = parseChartJson(row["chart Json"]);
                        const totalRaw = Number(row["chart Text"] ?? 0);
                        const total = Number.isFinite(totalRaw) ? totalRaw : 0;

                        return (
                           <div className="benefits-chart__slide-item" key={row.id ?? index}>
                              <DonutChart
                                 config={{
                                    title: row["chart Title"] ?? "",
                                    total: total,
                                    slices: slices,
                                 }}
                              />
                           </div>
                        );
                     })}
                  </Slider>
               )}
            </div>

            <div className="benefits-chart__footer">
               {slidesCount > 2 && (
                  <div className="benefits-chart__dots">
                     {sortedRows.map((_, index) => (
                        <button
                           type="button"
                           key={index}
                           className={clsx("benefits-chart__dot", {
                              "benefits-chart__dot--active": index === benefitIndex,
                           })}
                           aria-label={intl.formatMessage(
                              {
                                 id: "benefitsChart.goToSlide",
                                 defaultMessage: "Go to slide {slideNumber}",
                              },
                              { slideNumber: index + 1 },
                           )}
                           onClick={() => {
                              setBenefitIndex(index);
                              sliderRef.current?.slickGoTo(index);
                           }}
                        />
                     ))}
                  </div>
               )}
            </div>
         </div>
      </Widget>
   );
};
